import { z } from 'zod';

// Field shapes shared by the event planning contracts (money, agenda, program, registration, info).
// Shape only: the services keep their own ranges, lookups and messages.

const UUID = /^[0-9a-f-]{36}$/i;

export const EventRowId = z.string().regex(UUID, 'is not an id');

/** An id or key kept in the event's metadata (short random ids, audience keys, item ids). */
export const EventItemKey = z.string().min(1).max(200);

/** A member, contact or session id the service looks up itself (unknown ids answer 404 there). */
export const EventLookupId = z.string().max(200).nullish();

export const EventNoQuery = z.strictObject({});

/** Free text the service trims; `max` only where the service refuses longer text itself. */
export const EventText = (max?: number) =>
  (max ? z.string().refine((s) => s.trim().length <= max, `must be at most ${max} characters`) : z.string()).nullish();

/** Whole cents: a number, a digit string, or '' / null for none. */
export const EventCents = z
  .union([
    z.number().int('must be whole cents').min(0, 'must be whole cents'),
    z.string().regex(/^\d*$/, 'must be whole cents'),
    z.null(),
  ])
  .optional();

/** A whole number as a number, a digit string, or '' / null for none; ranges stay in the service. */
export const EventWhole = z
  .union([z.number(), z.string().regex(/^-?\d*$/, 'must be a whole number'), z.null()])
  .optional();

/** YYYY-MM-DD (a longer ISO string is cut to the day by the service), or '' / null for none. */
export const EventDay = z
  .union([z.string().regex(/^(\d{4}-\d{2}-\d{2}.*)?$/, 'is a date (YYYY-MM-DD)'), z.null()])
  .optional();

/** A yes/no as the services read it (booleans, and the strings and numbers bool() accepts). */
export const EventFlag = z
  .union([z.boolean(), z.enum(['true', 'false', 'yes', 'no', '']), z.literal(0), z.literal(1), z.null()])
  .optional();

/** An email list: an array, or one string split on commas, spaces and semicolons. */
export const EventEmailList = z
  .union([z.array(z.string().max(320)).max(100), z.string().max(10000), z.null()])
  .optional();

/** A list of keys or ids. */
export const EventKeyList = z.array(z.string().max(200)).max(500);

/** An enum that also takes '' or null for "none". */
export const EventOptionalEnum = <T extends readonly string[]>(values: T, message: string) =>
  z.union([z.enum(values, { error: message }), z.literal(''), z.null()]).optional();
